import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLive } from "@/contexts/LiveContext";
import AuthModal from "@/components/AuthModal";
import NotificationBell from "@/components/NotificationBell";
import { can } from "@/lib/roles";
import { LogOut, Shield, Sun, Moon } from "lucide-react";

function UserAvatar({ user, size = "w-8 h-8", textSize = "text-xs" }) {
  if (user?.avatar) return <img src={user.avatar} alt={user.name} className={`${size} rounded-full object-cover`} />;
  const colors = ['#7B2CBF', '#FFC300', '#F59E0B', '#16A34A', '#9D4EDD', '#EC4899'];
  const idx = (user?.name || 'U').charCodeAt(0) % colors.length;
  return (
    <div className={`${size} letter-avatar rounded-full`} style={{ backgroundColor: colors[idx] }}>
      <span className={textSize}>{(user?.name || 'U')[0].toUpperCase()}</span>
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { connected } = useLive();
  const [authOpen, setAuthOpen] = useState(false);
  const [authReason, setAuthReason] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const open = (e) => { setAuthReason(e?.detail?.reason || null); setAuthOpen(true); };
    window.addEventListener("fv-open-auth", open);
    return () => window.removeEventListener("fv-open-auth", open);
  }, []);

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100" data-testid="navbar">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <img src="/fv-logo.png" alt="FlippedView" className="w-9 h-9 rounded-[12px] shadow-sm object-cover" />
            <span className="font-heading font-extrabold text-lg sm:text-xl tracking-tight block">
              <span className="text-dark">Flipped</span><span className="text-view-b">View</span>
            </span>
            <span
              className={`hidden sm:flex items-center gap-1 text-[10px] font-semibold ${connected ? "text-success" : "text-neutral"}`}
              title={connected ? "Live updates connected" : "Reconnecting live updates…"}
              data-testid="live-status"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-success animate-pulse" : "bg-gray-300"}`} />
              {connected ? "LIVE" : "OFFLINE"}
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 text-neutral hover:text-dark transition-colors rounded-lg hover:bg-gray-50"
              data-testid="theme-toggle-btn"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {user ? (
              <>
                <NotificationBell />
                {can(user, "staff") && (
                  <button onClick={() => navigate('/admin')} className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-view-a bg-view-a-light rounded-lg hover:bg-view-a-light hover:brightness-95 transition-colors" data-testid="admin-panel-btn">
                    <Shield className="w-3.5 h-3.5" /> Admin
                  </button>
                )}
                <Link to={`/profile/${user.user_id}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="user-profile-link">
                  <UserAvatar user={user} />
                  <span className="hidden md:block text-sm font-medium text-dark">{user.name}</span>
                </Link>
                <button onClick={logout} className="p-2 text-neutral hover:text-view-b transition-colors rounded-lg hover:bg-gray-50" data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button onClick={() => setAuthOpen(true)} className="fv-gradient px-5 py-2 text-sm font-semibold text-white rounded-full hover:shadow-lg hover:shadow-view-a/30 hover:brightness-110 transition-all" data-testid="login-btn">
                Take a Side
              </button>
            )}
          </div>
        </div>
      </nav>
      <AuthModal open={authOpen} onClose={() => { setAuthOpen(false); setAuthReason(null); }} reason={authReason} />
    </>
  );
}

export { UserAvatar };
